import React from 'react';
import { getTrainingCourse, getTrainingLevel } from '../config/trainingCourse';
import TrainingCourse from './TrainingCourse';
import { formatCompactInteger } from '../utils/numberFormat';
import { t } from '../i18n';
import './GoalJourneyPanel.css';

function formatNumericDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
}

function formatGoalDate(dateValue) {
  if (!dateValue) return null;
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return formatNumericDate(date);
}

function formatDateFromToday(daysFromToday) {
  if (!Number.isFinite(daysFromToday) || daysFromToday < 0) return null;
  const date = new Date();
  date.setDate(date.getDate() + daysFromToday);
  return formatNumericDate(date);
}

export function getGoalJourney(summary = {}) {
  const goal = summary.practiceGoal || {};
  const currentScore = Math.max(0, Math.floor(Number(goal.currentScore ?? summary.exp ?? summary.totalScoreGained) || 0));
  const goalScore = Math.max(1, Math.floor(Number(goal.goalScore) || 500000));
  const progressPercent = Math.min(100, Math.max(0, (currentScore / goalScore) * 100));
  const pace = Number(goal.averageScorePerDayLast7) || 0;
  const providedDays = goal.estimatedDaysToGoal == null ? NaN : Number(goal.estimatedDaysToGoal);
  const derivedDays = pace >= 500 ? Math.ceil(Math.max(0, goalScore - currentScore) / pace) : null;
  const days = Number.isFinite(providedDays) && providedDays >= 0 ? providedDays : derivedDays;
  const goalDate = formatGoalDate(goal.estimatedGoalDate) || formatDateFromToday(days);
  const estimateAvailable = Boolean(
    pace >= 500
    && Number.isFinite(days)
    && days >= 0
    && goalDate
  );

  return {
    currentScore,
    goalScore,
    progressPercent,
    pace,
    days,
    goalDate,
    estimateAvailable,
  };
}

function GoalJourneyPanel({ summary = {} }) {
  const journey = getGoalJourney(summary);
  const level = getTrainingLevel(summary);
  const course = getTrainingCourse(level);
  const goalEstimate = (
    <div className="user-goal-estimate" aria-label={t('accountActivity.journey.estimate')} title={journey.estimateAvailable ? t('accountActivity.journey.estimate') : t('accountActivity.journey.buildPace')}>
      {journey.estimateAvailable ? (
        <>
          <time dateTime={journey.goalDate.replaceAll('/', '-')}>{journey.goalDate}</time>
          <span>(+{Math.ceil(journey.days)}D)</span>
        </>
      ) : <span aria-label={t('accountActivity.journey.buildPace')}>—</span>}
    </div>
  );

  return (
    <div className="user-goal-journey">
      <div className="user-goal-heading">
        <div className="user-goal-current">
          <span className="user-level-kicker">{t('accountActivity.user.level')}</span>
          <strong>Lv. {level}</strong>
          <span className="user-goal-score-caption" aria-label={`${t('accountActivity.journey.current')}: ${journey.currentScore}, ${t('accountActivity.journey.goal')}: ${journey.goalScore}`}>
            {formatCompactInteger(journey.currentScore)}/{formatCompactInteger(journey.goalScore)}
          </span>
        </div>
        <div className="user-goal-target">
          <span>{t('accountActivity.course.goal')}</span>
          <strong>Lv. {course.targetLevel}</strong>
          {goalEstimate}
        </div>
      </div>
      <TrainingCourse level={level} pace={journey.pace} goalEstimate={goalEstimate} />
    </div>
  );
}

export default GoalJourneyPanel;
